import { fmtDate, fmtDateTime } from '@/lib/format-date';
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, Plus, Trash2, Pencil, CheckCircle2, ChevronRight, ArrowLeft, Plug, XCircle } from 'lucide-react';
import { EroadLogo, BlackhawkLogo, NztaLogo, SendgridLogo, KudosityLogo, SmtpLogo, LogoProps } from './integration-logos';
import { api } from '@/lib/api';
import { EMPLOYEE_REF_QUERY } from '@/lib/ref-defaults';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from '@/components/ui/toast';
import { ScheduleBuilder, parseCron, describeCron } from '@/components/shared/schedule-builder';
import { AUTH_MODES, type AuthMode } from '@atlas/shared';
import { useUserActions } from '@/hooks/use-user-actions';
import { IotEmailTemplatesSubform } from '@/pages/master-data/iot-email-templates-subform';
import { AddressAutocomplete } from '@/components/shared/address-autocomplete';
import { LookupField, MultiLookupField } from '@/components/shared/lookup-field';
import { useConfirm } from '@/hooks/use-confirm';

type SectionId =
  | 'authentication'
  | 'session'
  | 'authorized-emails'
  | 'email-delivery'
  | 'iot-providers'
  | 'tracker'
  | 'asset-onboarding-configuration'
  | 'asset-transferring-configuration'
  | 'integrations'
  | 'run-sheet-schedules';

const SECTIONS: Array<{ id: SectionId; label: string; description: string }> = [
  { id: 'authentication',                     label: 'Authentication',                     description: 'Sign-in policy.' },
  { id: 'session',                            label: 'Session',                            description: 'Idle timeout before the user is sent back to the login screen.' },
  { id: 'authorized-emails',                  label: 'Authorized Emails',                  description: 'Email senders used for system notifications.' },
  { id: 'email-delivery',                     label: 'Email Delivery',                     description: 'Transport used to send system email (SMTP or SendGrid).' },
  { id: 'iot-providers',                      label: 'IoT Providers',                      description: 'External IoT/telematics suppliers.' },
  { id: 'tracker',                            label: 'Tracker Settings',                   description: 'Default colour + map location for the tracker.' },
  { id: 'asset-onboarding-configuration',     label: 'Asset Onboarding Configuration',     description: 'Task assignments used when onboarding a new asset.' },
  { id: 'asset-transferring-configuration',   label: 'Asset Transferring Configuration',   description: 'Task assignments used when transferring an asset.' },
  // API keys now live on their own page — Admin → Developer
  // (client/src/pages/admin/developer.tsx).
  { id: 'integrations',                       label: 'Integrations',                       description: 'API credentials for external services (NZTA, etc.).' },
  { id: 'run-sheet-schedules',                label: 'Run Sheet Schedules',                description: 'Recurring run-sheet emails — only admins can see who they go to.' },
];

export function SettingsPage() {
  const [section, setSection] = React.useState<SectionId>('authentication');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="text-sm text-muted-foreground">System-wide configuration.</p>
      </div>

      <div className="flex flex-col lg:flex-row lg:gap-6 gap-3">
        {/* Mobile / tablet: section picker as a Select dropdown. The
            sidebar nav below `lg` would either eat half the screen
            (vertical) or wrap awkwardly (horizontal scroll), so we
            collapse it into a single dropdown on small screens. */}
        <div className="lg:hidden">
          <Select value={section} onValueChange={(v) => setSection(v as SectionId)}>
            <SelectTrigger aria-label="Settings section"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SECTIONS.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Desktop: persistent sidebar nav. */}
        <nav className="hidden lg:block lg:w-56 shrink-0 space-y-1">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              onClick={() => setSection(s.id)}
              className={`w-full text-left px-3 py-2 rounded-md text-sm transition ${
                section === s.id ? 'bg-accent text-accent-foreground font-medium' : 'hover:bg-accent/50'
              }`}
            >
              {s.label}
            </button>
          ))}
        </nav>

        <div className="flex-1 min-w-0">
          {section === 'authentication'                   && <AuthenticationSection />}
          {section === 'session'                          && <SessionSection />}
          {section === 'authorized-emails'                && <AuthorizedEmailsSection />}
          {section === 'email-delivery'                   && <EmailDeliverySection />}
          {section === 'iot-providers'                    && <IotProvidersSection />}
          {section === 'tracker'                          && <TrackerSettingsSection />}
          {section === 'asset-onboarding-configuration'   && <AssetSettingConfigSection kind="onboard"  title="Asset Onboarding Configuration"   description="Task assignments used when onboarding a new asset." />}
          {section === 'asset-transferring-configuration' && <AssetSettingConfigSection kind="transfer" title="Asset Transferring Configuration" description="Task assignments used when transferring an asset between companies, departments, or sites." />}
          {section === 'integrations'                     && <IntegrationsSection />}
          {section === 'run-sheet-schedules'              && <RunSheetSchedulesSection />}
        </div>
      </div>
    </div>
  );
}

// ---------------- Authentication ----------------

function useSetting(key: string) {
  const { data } = useQuery({
    queryKey: ['/master-data/app-settings', key],
    queryFn: async () => {
      const r = await api<any[]>(`/master-data/app-settings?search=${encodeURIComponent(key)}`);
      return (r.data || []).find((s: any) => s.key === key) || null;
    },
  });
  return data;
}

function AuthenticationSection() {
  const qc = useQueryClient();
  const authMode = useSetting('authMode');
  const [val, setVal] = React.useState<AuthMode>('both');
  React.useEffect(() => { if (authMode?.value) setVal(authMode.value as AuthMode); }, [authMode]);

  const save = useMutation({
    mutationFn: async () => {
      const body = { key: 'authMode', value: val };
      const r = authMode?.id
        ? await api(`/master-data/app-settings/${authMode.id}`, { method: 'PUT', body: JSON.stringify(body) })
        : await api('/master-data/app-settings', { method: 'POST', body: JSON.stringify(body) });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
    },
    onSuccess: () => { toast('Saved', undefined, 'success'); qc.invalidateQueries({ queryKey: ['/master-data/app-settings'] }); },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Authentication</CardTitle>
        <CardDescription>How employees can sign in.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label>Auth Mode</Label>
          <Select value={val} onValueChange={(v) => setVal(v as AuthMode)}>
            <SelectTrigger className="max-w-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {AUTH_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" onClick={() => save.mutate()}><Save className="h-4 w-4" /> Save</Button>
      </CardContent>
    </Card>
  );
}

// ---------------- Session ----------------
//
// Idle-timeout policy. The value is stored on the AppSetting model with the
// key `session.idleTimeoutMinutes`. The auth store applies it on every
// /auth/me load via `applyIdleTimeoutFromSettings()`, so the change rolls
// out to every tab the next time their session is verified — no deploy
// needed. Existing tabs pick up the new value on their next refresh tick.

const SESSION_KEY = 'session.idleTimeoutMinutes';
const SESSION_DEFAULT_MIN = 30;

function SessionSection() {
  const qc = useQueryClient();
  const setting = useSetting(SESSION_KEY);
  const [val, setVal] = React.useState<string>(String(SESSION_DEFAULT_MIN));
  React.useEffect(() => {
    if (setting?.value != null) setVal(String(setting.value));
    else setVal(String(SESSION_DEFAULT_MIN));
  }, [setting]);

  // Validate: must be a non-negative integer. 0 disables idle logout
  // entirely (sessions only end on explicit logout or refresh-cookie
  // expiry, i.e. the 30-day ceiling).
  const minutes = Number.parseInt(val, 10);
  const valid = Number.isFinite(minutes) && minutes >= 0 && minutes <= 24 * 60;

  const save = useMutation({
    mutationFn: async () => {
      if (!valid) throw new Error('Enter a whole number between 0 and 1440.');
      const body = { key: SESSION_KEY, value: minutes };
      const r = setting?.id
        ? await api(`/master-data/app-settings/${setting.id}`, { method: 'PUT', body: JSON.stringify(body) })
        : await api('/master-data/app-settings', { method: 'POST', body: JSON.stringify(body) });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
    },
    onSuccess: () => {
      toast('Saved', 'Idle timeout updated. Open tabs pick up the new value on their next session refresh.', 'success');
      qc.invalidateQueries({ queryKey: ['/master-data/app-settings'] });
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Session</CardTitle>
        <CardDescription>
          How long a tab can sit idle before the user is bounced back to /auth/login. Active users (mouse, keyboard,
          scroll, touch) reset the timer; the silent token refresh keeps an active session alive indefinitely up to
          the 30-day refresh-cookie ceiling.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label>Idle timeout (minutes)</Label>
          <div className="flex items-center gap-2 mt-1">
            <Input
              type="number"
              value={val}
              onChange={(e) => setVal(e.target.value)}
              min={0}
              max={24 * 60}
              step={1}
              className="max-w-[160px]"
            />
            <span className="text-xs text-muted-foreground">
              0 disables auto-logout; max 1440 (24 hours).
            </span>
          </div>
          {!valid && (
            <p className="text-xs text-destructive mt-1">Enter a whole number between 0 and 1440.</p>
          )}
        </div>
        <Button size="sm" disabled={!valid || save.isPending} onClick={() => save.mutate()}>
          <Save className="h-4 w-4" /> Save
        </Button>
      </CardContent>
    </Card>
  );
}

// ---------------- Authorized Emails ----------------

function AuthorizedEmailsSection() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { data = [] } = useQuery({
    queryKey: ['/master-data/authorized-emails'],
    queryFn: async () => (await api<any[]>('/master-data/authorized-emails?limit=500')).data || [],
  });
  const [draft, setDraft] = React.useState('');

  const add = useMutation({
    mutationFn: async () => {
      const r = await api('/master-data/authorized-emails', { method: 'POST', body: JSON.stringify({ email: draft }) });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
    },
    onSuccess: () => { setDraft(''); toast('Added', undefined, 'success'); qc.invalidateQueries({ queryKey: ['/master-data/authorized-emails'] }); },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });
  const del = useMutation({
    mutationFn: async (id: string) => {
      const r = await api(`/master-data/authorized-emails/${id}`, { method: 'DELETE' });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['/master-data/authorized-emails'] }),
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Authorized Emails</CardTitle>
        <CardDescription>Sender addresses available for system-generated email templates.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Input
            placeholder="new@example.com"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            type="email"
            className="max-w-sm"
          />
          <Button size="sm" onClick={() => add.mutate()} disabled={!draft || add.isPending}>
            <Plus className="h-4 w-4" /> Add
          </Button>
        </div>
        <div className="border rounded-md divide-y">
          {data.length === 0 && <div className="p-3 text-sm text-muted-foreground">No authorized emails.</div>}
          {data.map((row: any) => (
            <div key={row.id} className="flex items-center justify-between px-3 py-2 text-sm">
              <span>{row.email}</span>
              <button
                className="text-destructive hover:underline text-xs flex items-center gap-1"
                onClick={async () => {
                  const ok = await confirm({
                    title: 'Remove authorized email?',
                    description: (
                      <>
                        <span className="font-medium">{row.email}</span>{' '}
                        will no longer be available as a sender for system-generated emails.
                        Existing templates that use it will fall back to the system default.
                      </>
                    ),
                    confirmLabel: 'Remove',
                    destructive: true,
                  });
                  if (ok) del.mutate(row.id);
                }}
              >
                <Trash2 className="h-3 w-3" /> Remove
              </button>
            </div>
          ))}
        </div>
      </CardContent>

    </Card>
  );
}

// ---------------- Email Delivery ----------------
//
// Transport toggle for outbound system email. Stored on the AppSetting model
// under `email.provider` and read by `sendEmail` via `getEmailProvider()`
// (DB-first, env `EMAIL_PROVIDER` fallback). No deploy needed — the 30s
// integration cache picks up the change.

const EMAIL_PROVIDER_KEY = 'email.provider';

function EmailDeliverySection() {
  const qc = useQueryClient();
  const setting = useSetting(EMAIL_PROVIDER_KEY);
  const [val, setVal] = React.useState<'smtp' | 'sendgrid'>('smtp');
  React.useEffect(() => {
    if (setting?.value === 'smtp' || setting?.value === 'sendgrid') setVal(setting.value);
  }, [setting]);

  const save = useMutation({
    mutationFn: async () => {
      const body = { key: EMAIL_PROVIDER_KEY, value: val };
      const r = setting?.id
        ? await api(`/master-data/app-settings/${setting.id}`, { method: 'PUT', body: JSON.stringify(body) })
        : await api('/master-data/app-settings', { method: 'POST', body: JSON.stringify(body) });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
    },
    onSuccess: () => { toast('Saved', 'Email transport updated. Applies within ~30s.', 'success'); qc.invalidateQueries({ queryKey: ['/master-data/app-settings'] }); },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Email Delivery</CardTitle>
        <CardDescription>Transport used for all outbound system email — magic links, invitations, run-sheet dispatches, and notifications.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="max-w-sm">
          <Label>Provider</Label>
          <Select value={val} onValueChange={(v) => setVal(v as 'smtp' | 'sendgrid')}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="smtp">SMTP</SelectItem>
              <SelectItem value="sendgrid">SendGrid</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-muted-foreground">
          SendGrid also requires an API key under <span className="font-medium">Integrations → Email (SendGrid)</span>. If SendGrid is selected but no key is configured (or a send fails), email automatically falls back to SMTP.
        </p>
        <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}><Save className="h-4 w-4" /> Save</Button>
      </CardContent>
    </Card>
  );
}

// ---------------- IoT Providers ----------------

function IotProvidersSection() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { data = [] } = useQuery({
    queryKey: ['/master-data/iot-suppliers'],
    queryFn: async () => (await api<any[]>('/master-data/iot-suppliers?limit=500')).data || [],
  });
  const [draft, setDraft] = React.useState<{ providerName: string; email: string; phone: string }>({ providerName: '', email: '', phone: '' });
  // Edit-dialog state — opening loads the full row (incl. embedded onboard /
  // offboard email templates) so the inline editor can mutate them in place.
  const [editForm, setEditForm] = React.useState<Record<string, any> | null>(null);

  const add = useMutation({
    mutationFn: async () => {
      const r = await api('/master-data/iot-suppliers', {
        method: 'POST',
        body: JSON.stringify({ providerName: draft.providerName, email: draft.email || null, phone: draft.phone || null }),
      });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
    },
    onSuccess: () => { setDraft({ providerName: '', email: '', phone: '' }); toast('Added', undefined, 'success'); qc.invalidateQueries({ queryKey: ['/master-data/iot-suppliers'] }); },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });
  const update = useMutation({
    mutationFn: async (body: Record<string, any>) => {
      if (!editForm?.id) throw new Error('No record loaded');
      const { id, _id, __v, createdAt, updatedAt, ...payload } = body;
      const r = await api(`/master-data/iot-suppliers/${editForm.id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
    },
    onSuccess: () => {
      toast('Saved', undefined, 'success');
      qc.invalidateQueries({ queryKey: ['/master-data/iot-suppliers'] });
      setEditForm(null);
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });
  const del = useMutation({
    mutationFn: async (id: string) => {
      const r = await api(`/master-data/iot-suppliers/${id}`, { method: 'DELETE' });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['/master-data/iot-suppliers'] }),
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">IoT Providers</CardTitle>
        <CardDescription>External telematics / IoT providers referenced by assets.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Add-row: stacked single column on mobile, 4-col on lg.
            The "Add" button takes full width on mobile so it stays
            tappable. */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_auto] gap-2 lg:items-end">
          <div><Label className="text-xs">Provider</Label><Input value={draft.providerName} onChange={(e) => setDraft((d) => ({ ...d, providerName: e.target.value }))} /></div>
          <div><Label className="text-xs">Email</Label><Input type="email" value={draft.email} onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))} /></div>
          <div><Label className="text-xs">Phone</Label><Input value={draft.phone} onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))} /></div>
          <Button size="sm" className="w-full lg:w-auto" onClick={() => add.mutate()} disabled={!draft.providerName || add.isPending}>
            <Plus className="h-4 w-4" /> Add
          </Button>
        </div>
        {data.length === 0 && (
          <div className="border rounded-md px-3 py-3 text-sm text-muted-foreground">No providers.</div>
        )}

        {/* Mobile / tablet: stacked cards. Same pattern as the task list
            and dispatch products subform — the 4-column table doesn't
            shrink below ~500px without clipping the action column. */}
        {data.length > 0 && (
          <div className="lg:hidden space-y-2">
            {data.map((row: any) => (
              <div key={row.id} className="border rounded-md p-3">
                <div className="font-medium text-sm">{row.providerName}</div>
                <div className="mt-1 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-xs">
                  <span className="text-muted-foreground">Email</span>
                  <span className="truncate">{row.email || '—'}</span>
                  <span className="text-muted-foreground">Phone</span>
                  <span>{row.phone || '—'}</span>
                </div>
                <div className="mt-2 flex items-center justify-end gap-3">
                  <button
                    className="text-primary hover:underline text-xs"
                    onClick={() => setEditForm({ ...row })}
                  >Edit</button>
                  <button
                    className="text-destructive hover:underline text-xs"
                    onClick={async () => { if (await confirm({ title: `Remove ${row.providerName}?`, description: 'This IoT provider will no longer be available for new assets.', destructive: true, confirmLabel: 'Remove' })) del.mutate(row.id); }}
                  >Remove</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Desktop: full table. */}
        {data.length > 0 && (
          <div className="hidden lg:block border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs">
                <tr>
                  <th className="text-left px-3 py-2">Provider</th>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-left px-3 py-2">Phone</th>
                  <th className="w-16"></th>
                </tr>
              </thead>
              <tbody>
                {data.map((row: any) => (
                  <tr key={row.id} className="border-t">
                    <td className="px-3 py-2">{row.providerName}</td>
                    <td className="px-3 py-2 text-muted-foreground">{row.email || '—'}</td>
                    <td className="px-3 py-2 text-muted-foreground">{row.phone || '—'}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        className="text-primary hover:underline text-xs mr-3"
                        onClick={() => setEditForm({ ...row })}
                      >Edit</button>
                      <button
                        className="text-destructive hover:underline text-xs"
                        onClick={async () => { if (await confirm({ title: `Remove ${row.providerName}?`, description: 'This IoT provider will no longer be available for new assets.', destructive: true, confirmLabel: 'Remove' })) del.mutate(row.id); }}
                      >Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {/* Edit dialog — mirrors the master-data /iot-suppliers edit form so the
          full email template editor is available without leaving Settings. */}
      <Dialog open={!!editForm} onOpenChange={(o) => { if (!o) setEditForm(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit IoT Provider</DialogTitle>
          </DialogHeader>
          {editForm && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Provider Name</Label>
                <Input
                  value={editForm.providerName ?? ''}
                  onChange={(e) => setEditForm((p) => ({ ...(p ?? {}), providerName: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input
                    type="email"
                    value={editForm.email ?? ''}
                    onChange={(e) => setEditForm((p) => ({ ...(p ?? {}), email: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input
                    value={editForm.phone ?? ''}
                    onChange={(e) => setEditForm((p) => ({ ...(p ?? {}), phone: e.target.value }))}
                  />
                </div>
              </div>

              <IotEmailTemplatesSubform
                isEdit
                parentId={editForm.id}
                formValues={editForm}
                setFormValues={setEditForm as React.Dispatch<React.SetStateAction<Record<string, any>>>}
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditForm(null)}>Cancel</Button>
            <Button onClick={() => editForm && update.mutate(editForm)} disabled={update.isPending}>
              {update.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ---------------- Tracker Settings ----------------

function TrackerSettingsSection() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['/tracker-settings'],
    queryFn: async () => (await api<any[]>('/tracker-settings?limit=1')).data?.[0] || null,
  });
  const [primaryColour, setPrimaryColour] = React.useState('');
  // Single combined "address" string. Persisted to `defaultMapLocation.addressLine1`
  // (the schema's only required-shape field) and reconstituted from a
  // legacy `{ addressLine1, city }` shape on read so existing records
  // round-trip cleanly.
  const [address, setAddress] = React.useState('');
  React.useEffect(() => {
    if (!data) return;
    setPrimaryColour(data.primaryColour || '');
    const loc = data.defaultMapLocation;
    setAddress([loc?.addressLine1, loc?.city].filter(Boolean).join(', '));
  }, [data]);

  const save = useMutation({
    mutationFn: async () => {
      const trimmed = address.trim();
      const body = {
        primaryColour: primaryColour || null,
        defaultMapLocation: trimmed ? { addressLine1: trimmed } : null,
      };
      const r = data?.id
        ? await api(`/tracker-settings/${data.id}`, { method: 'PUT', body: JSON.stringify(body) })
        : await api('/tracker-settings', { method: 'POST', body: JSON.stringify(body) });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
    },
    onSuccess: () => { toast('Saved', undefined, 'success'); qc.invalidateQueries({ queryKey: ['/tracker-settings'] }); },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Tracker Settings</CardTitle>
        <CardDescription>Defaults used by the live tracker / map views.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="max-w-sm">
          <Label>Primary Colour (hex)</Label>
          <Input value={primaryColour} onChange={(e) => setPrimaryColour(e.target.value)} placeholder="#0066CC" />
        </div>
        <div className="max-w-lg">
          <Label>Default Address</Label>
          <AddressAutocomplete
            value={address}
            onChange={setAddress}
            // Use the full formatted place name on selection — that's the
            // string Mapbox can geocode round-trip without ambiguity.
            onSelect={(f) => setAddress(f.place_name)}
            placeholder="Start typing an address…"
          />
        </div>
        <Button size="sm" onClick={() => save.mutate()}><Save className="h-4 w-4" /> Save</Button>
      </CardContent>
    </Card>
  );
}

// ---------------- Asset Setting Configuration (onboard + transfer) ----------------
// Generic editor used by both `Asset Onboarding Configuration` (kind=onboard)
// and `Asset Transferring Configuration` (kind=transfer). Each configuration
// record is an AssetSetting; its task assignments live in AssetSettingTask.

type Kind = 'onboard' | 'transfer';

function AssetSettingConfigSection({ kind, title, description }: { kind: Kind; title: string; description: string }) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { actions: granted } = useUserActions();
  const canActivate = granted.has('asset-setting.activate');
  const listKey = ['/asset-settings', kind];
  const { data: settings = [] } = useQuery<any[]>({
    queryKey: listKey,
    queryFn: async () => (await api<any[]>(`/asset-settings?kind=${kind}&limit=200`)).data || [],
  });

  const [draftName, setDraftName] = React.useState('');
  const [expandedId, setExpandedId] = React.useState<string | null>(null);

  const addSetting = useMutation({
    mutationFn: async () => {
      const r = await api('/asset-settings', {
        method: 'POST',
        body: JSON.stringify({ kind, settingsName: draftName, apply: false }),
      });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
    },
    onSuccess: () => { setDraftName(''); qc.invalidateQueries({ queryKey: listKey }); toast('Added', undefined, 'success'); },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });
  const activate = useMutation({
    mutationFn: async (id: string) => {
      const r = await api(`/asset-settings/${id}/activate`, { method: 'POST' });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: listKey }); toast('Activated', undefined, 'success'); },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });
  const renameSetting = useMutation({
    mutationFn: async (args: { id: string; settingsName: string }) => {
      const r = await api(`/asset-settings/${args.id}`, { method: 'PUT', body: JSON.stringify({ settingsName: args.settingsName }) });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: listKey }),
    onError: (e: any) => toast('Error', e.message, 'error'),
  });
  const delSetting = useMutation({
    mutationFn: async (id: string) => {
      const r = await api(`/asset-settings/${id}`, { method: 'DELETE' });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: listKey }); setExpandedId(null); },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Input
            placeholder="New configuration name…"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            className="max-w-sm"
          />
          <Button size="sm" onClick={() => addSetting.mutate()} disabled={!draftName || addSetting.isPending}>
            <Plus className="h-4 w-4" /> Add
          </Button>
        </div>

        <div className="border rounded-md divide-y">
          {settings.length === 0 && <div className="p-3 text-sm text-muted-foreground">No configurations.</div>}
          {settings.map((s: any) => {
            const open = expandedId === s.id;
            return (
              <div key={s.id}>
                <div className="flex items-center gap-3 flex-wrap px-3 py-2 text-sm">
                  <button
                    onClick={() => setExpandedId(open ? null : s.id)}
                    className="font-medium hover:underline text-left min-w-0 truncate"
                  >{open ? '▼' : '▶'} {s.settingsName || '(unnamed)'}</button>
                  {s.apply && <Badge variant="success">Active</Badge>}
                  <div className="flex-1 hidden sm:block" />
                  {!s.apply && canActivate && (
                    <Button
                      size="sm" variant="outline"
                      disabled={activate.isPending}
                      onClick={async () => { if (await confirm({ title: `Activate "${s.settingsName}"?`, description: 'The current active configuration will become inactive.', confirmLabel: 'Activate' })) activate.mutate(s.id); }}
                    >
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Activate
                    </Button>
                  )}
                  <button
                    className="text-muted-foreground hover:text-foreground text-xs"
                    onClick={() => {
                      const next = window.prompt('Rename configuration:', s.settingsName ?? '');
                      if (next != null && next !== s.settingsName) renameSetting.mutate({ id: s.id, settingsName: next });
                    }}
                  ><Pencil className="h-3 w-3" /></button>
                  <button
                    className="text-destructive hover:underline text-xs flex items-center gap-1"
                    onClick={async () => { if (await confirm({ title: `Remove configuration "${s.settingsName}"?`, destructive: true, confirmLabel: 'Remove' })) delSetting.mutate(s.id); }}
                  ><Trash2 className="h-3 w-3" /></button>
                </div>
                {open && <TaskAssignmentsPanel settingId={s.id} kind={kind} />}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

const TASK_FIELD_TYPES: Array<{ value: 'checkbox'|'text'|'number'|'date'|'select'|'ref'; label: string }> = [
  { value: 'checkbox', label: 'Checkbox (yes/no)' },
  { value: 'text',     label: 'Text' },
  { value: 'number',   label: 'Number' },
  { value: 'date',     label: 'Date' },
  { value: 'select',   label: 'Select (dropdown)' },
  { value: 'ref',      label: 'Lookup (other record)' },
];

// Common ref endpoints admins can pick when configuring a Lookup task.
const REF_ENDPOINT_PRESETS: Array<{ value: string; label: string; refLabel: string }> = [
  { value: '/employees',                    label: 'Employees',           refLabel: 'fullName' },
  { value: '/master-data/companies',        label: 'Companies',           refLabel: 'name' },
  { value: '/master-data/branches',         label: 'Branches',            refLabel: 'name' },
  { value: '/master-data/sites',            label: 'Sites',               refLabel: 'name' },
  { value: '/master-data/depots',           label: 'Depots',              refLabel: 'name' },
  { value: '/master-data/service-locations',label: 'Service Locations',   refLabel: 'name' },
  { value: '/master-data/departments',      label: 'Departments',         refLabel: 'name' },
  { value: '/master-data/vehicle-types',    label: 'Vehicle Types',       refLabel: 'type' },
  { value: '/master-data/vehicle-groups',   label: 'Vehicle Groups',      refLabel: 'group' },
  { value: '/master-data/vendors',          label: 'Vendors',             refLabel: 'name' },
  { value: '/fleet/assets',                 label: 'Assets',              refLabel: 'fleetNumber' },
];

function TaskAssignmentsPanel({ settingId }: { settingId: string; kind: Kind }) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const key = ['/asset-setting-tasks', settingId];
  const { data: tasks = [] } = useQuery<any[]>({
    queryKey: key,
    queryFn: async () => (await api<any[]>(`/asset-setting-tasks?setting=${settingId}&limit=500`)).data || [],
  });

  const [editing, setEditing] = React.useState<any | null>(null);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = React.useState(false);

  // Per-row + bulk delete. Server enforces the same `delete` permission
  // on the underlying CRUD route; the buttons just expose the affordance.
  const deleteOne = useMutation({
    mutationFn: async (id: string) => {
      const r = await api(`/asset-setting-tasks/${id}`, { method: 'DELETE' });
      if (!r.success) throw new Error(r.error?.message || 'Delete failed');
    },
    onSuccess: () => {
      toast('Task removed', undefined, 'success');
      qc.invalidateQueries({ queryKey: key });
    },
    onError: (e: any) => toast('Error', e?.message ?? String(e), 'error'),
  });
  const deleteMany = useMutation({
    mutationFn: async (ids: string[]) => {
      // Sequential rather than parallel — 24-40 tasks max, no need to
      // fan out and risk swamping the API or the Atlas free-tier limits.
      for (const id of ids) {
        const r = await api(`/asset-setting-tasks/${id}`, { method: 'DELETE' });
        if (!r.success) throw new Error(r.error?.message || `Delete failed for ${id}`);
      }
    },
    onSuccess: () => {
      toast('Tasks removed', undefined, 'success');
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: key });
    },
    onError: (e: any) => toast('Error', e?.message ?? String(e), 'error'),
  });
  async function handleDeleteOne(t: any) {
    const ok = await confirm({
      title: `Remove task "${t.pointName || '(unnamed)'}"?`,
      description: 'This permanently deletes the task from this configuration. Answers on existing onboardings/transfers are not affected.',
      destructive: true,
      confirmLabel: 'Remove',
    });
    if (ok) deleteOne.mutate(t.id);
  }
  async function handleDeleteSelected() {
    const ok = await confirm({
      title: `Remove ${selected.size} task(s)?`,
      description: 'This permanently deletes the selected tasks from this configuration.',
      destructive: true,
      confirmLabel: 'Remove',
    });
    if (ok) deleteMany.mutate([...selected]);
  }

  const sortedTasks = React.useMemo(
    () => [...tasks].sort((a: any, b: any) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    [tasks],
  );
  const allSelected = sortedTasks.length > 0 && selected.size === sortedTasks.length;

  function toggleAll() {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(sortedTasks.map((t: any) => t.id)));
  }
  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  return (
    <div className="bg-muted/20 px-3 py-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Tasks</div>
        <span className="text-[11px] text-muted-foreground italic">Edit or remove tasks per row.</span>
      </div>

      {selected.size > 0 && (
        <div className="flex items-center gap-2 px-2 py-1.5 rounded border bg-primary/5 text-xs">
          <span className="font-medium">{selected.size} selected</span>
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)}>
            Bulk assign
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="text-destructive hover:text-destructive"
            disabled={deleteMany.isPending}
            onClick={handleDeleteSelected}
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" />
            {deleteMany.isPending ? 'Removing…' : 'Remove'}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
        </div>
      )}

      {sortedTasks.length === 0 && (
        <div className="border rounded bg-background px-3 py-2 text-xs text-muted-foreground">
          No tasks yet.
        </div>
      )}

      {/* Mobile / tablet: cards. The desktop table has 8 columns and
          can't shrink below ~760px without clipping; on `<lg` we
          render each task as a card with label/value rows so the
          whole panel stays usable on a phone. The select-all toggle
          gets its own header strip since there's no table header. */}
      {sortedTasks.length > 0 && (
        <div className="lg:hidden space-y-2">
          <label className="flex items-center gap-2 text-xs px-1">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
            />
            <span className="text-muted-foreground">Select all ({sortedTasks.length})</span>
          </label>
          {sortedTasks.map((t: any) => {
            const others = Array.isArray(t.otherStaff) && t.otherStaff.length > 0
              ? t.otherStaff.map((s: any) => s?.fullName || s?.id).filter(Boolean).join(', ')
              : '—';
            return (
              <div
                key={t.id}
                className="border rounded bg-background p-3 cursor-pointer hover:bg-accent/30"
                onClick={() => setEditing(t)}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={selected.has(t.id)}
                    onChange={() => toggleOne(t.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-medium text-sm truncate">{t.pointName || '—'}</div>
                      <div className="flex items-center gap-1 shrink-0">
                        {t.stage && (
                          <Badge variant="secondary" className="text-[10px]">{t.stage}</Badge>
                        )}
                        <button
                          type="button"
                          className="p-1 text-destructive hover:text-destructive/80"
                          aria-label="Remove task"
                          title="Remove task"
                          onClick={(e) => { e.stopPropagation(); handleDeleteOne(t); }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-1 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-[11px]">
                      <span className="text-muted-foreground">Order</span>
                      <span>{t.sortOrder ?? 0}{t.required && <span className="ml-2 text-destructive">required</span>}</span>
                      <span className="text-muted-foreground">Input</span>
                      <span>{t.fieldType || 'checkbox'}</span>
                      <span className="text-muted-foreground">Asset field</span>
                      <span className="font-mono text-[10px] truncate">{t.assetField || '—'}</span>
                      <span className="text-muted-foreground">Responsible</span>
                      <span className="truncate">{t.responsiblePerson?.fullName || '—'}</span>
                      <span className="text-muted-foreground">Other staff</span>
                      <span className="truncate">{others}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Desktop: full 8-column table. */}
      {sortedTasks.length > 0 && (
        <div className="hidden lg:block border rounded bg-background overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/40">
              <tr>
                <th className="px-2 py-1 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    onClick={(e) => e.stopPropagation()}
                  />
                </th>
                <th className="text-left px-2 py-1 w-32">Stage</th>
                <th className="text-left px-2 py-1 w-12">#</th>
                <th className="text-left px-2 py-1">Task</th>
                <th className="text-left px-2 py-1 w-24">Input</th>
                <th className="text-left px-2 py-1 w-44">Updates Asset Field</th>
                <th className="text-left px-2 py-1 w-44">Responsible</th>
                <th className="text-left px-2 py-1 w-44">Other Staff</th>
                <th className="px-2 py-1 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {sortedTasks.map((t: any) => (
                <tr
                  key={t.id}
                  className="border-t cursor-pointer hover:bg-accent/30"
                  onClick={() => setEditing(t)}
                >
                  <td className="px-2 py-1" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(t.id)}
                      onChange={() => toggleOne(t.id)}
                    />
                  </td>
                  <td className="px-2 py-1 font-medium">{t.stage || <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-2 py-1 text-muted-foreground">{t.sortOrder ?? 0}</td>
                  <td className="px-2 py-1">
                    <div>{t.pointName || '—'}</div>
                    {t.required && <span className="text-[10px] text-destructive">required</span>}
                  </td>
                  <td className="px-2 py-1">{t.fieldType || 'checkbox'}</td>
                  <td className="px-2 py-1 font-mono text-[11px]">{t.assetField || '—'}</td>
                  <td className="px-2 py-1 text-muted-foreground">{t.responsiblePerson?.fullName || '—'}</td>
                  <td className="px-2 py-1 text-muted-foreground">
                    {Array.isArray(t.otherStaff) && t.otherStaff.length > 0
                      ? t.otherStaff.map((s: any) => s?.fullName || s?.id).filter(Boolean).join(', ')
                      : '—'}
                  </td>
                  <td className="px-2 py-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="p-1 text-destructive hover:text-destructive/80"
                      aria-label="Remove task"
                      title="Remove task"
                      onClick={() => handleDeleteOne(t)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <TaskEditDialog
          task={editing}
          settingId={settingId}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: key }); }}
        />
      )}

      {bulkOpen && (
        <BulkAssignDialog
          taskIds={[...selected]}
          onClose={() => setBulkOpen(false)}
          onSaved={() => {
            setBulkOpen(false);
            setSelected(new Set());
            qc.invalidateQueries({ queryKey: key });
          }}
        />
      )}
    </div>
  );
}

function BulkAssignDialog({
  taskIds, onClose, onSaved,
}: { taskIds: string[]; onClose: () => void; onSaved: () => void }) {
  const { data: employees = [] } = useQuery<any[]>({
    queryKey: ['ref', '/employees', EMPLOYEE_REF_QUERY],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '500', ...EMPLOYEE_REF_QUERY });
      return (await api<any[]>(`/employees?${params.toString()}`)).data || [];
    },
  });
  // Empty string = leave that field untouched on apply.
  const [responsiblePerson, setResponsiblePerson] = React.useState<string>('');
  const [setResponsibleEnabled, setSetResponsibleEnabled] = React.useState(false);
  const [otherStaff, setOtherStaff] = React.useState<string[]>([]);
  const [setOtherEnabled, setSetOtherEnabled] = React.useState(false);
  const [otherMode, setOtherMode] = React.useState<'replace' | 'add'>('replace');

  const apply = useMutation({
    mutationFn: async () => {
      for (const id of taskIds) {
        const patch: Record<string, unknown> = {};
        if (setResponsibleEnabled) {
          patch.responsiblePerson = responsiblePerson || null;
        }
        if (setOtherEnabled) {
          if (otherMode === 'replace') {
            patch.otherStaff = otherStaff;
          } else {
            // Merge with existing — fetch current then union.
            const r = await api<any>(`/asset-setting-tasks/${id}`);
            const cur: string[] = Array.isArray(r.data?.otherStaff)
              ? r.data.otherStaff.map((s: any) => s?.id ?? s?._id ?? s).filter(Boolean)
              : [];
            patch.otherStaff = Array.from(new Set([...cur, ...otherStaff]));
          }
        }
        if (Object.keys(patch).length === 0) continue;
        const r = await api(`/asset-setting-tasks/${id}`, {
          method: 'PUT',
          body: JSON.stringify(patch),
        });
        if (!r.success) throw new Error(r.error?.message || `Failed for ${id}`);
      }
    },
    onSuccess: () => { toast(`Updated ${taskIds.length} task(s)`, undefined, 'success'); onSaved(); },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  function toggleOther(id: string) {
    setOtherStaff((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  }

  const nothingToApply = !setResponsibleEnabled && !setOtherEnabled;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Bulk assign · {taskIds.length} task(s)</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm max-h-[60vh] overflow-y-auto pr-1">
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <Checkbox
                checked={setResponsibleEnabled}
                onCheckedChange={(v) => setSetResponsibleEnabled(!!v)}
              />
              <span className="font-medium">Set Responsible Person</span>
            </label>
            {setResponsibleEnabled && (
              <Select
                value={responsiblePerson || '__none__'}
                onValueChange={(v) => setResponsiblePerson(v === '__none__' ? '' : v)}
              >
                <SelectTrigger><SelectValue placeholder="— Choose —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Clear (no one) —</SelectItem>
                  {employees.map((e: any) => (
                    <SelectItem key={e.id} value={e.id}>{e.fullName || e.email || e.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-2 border-t pt-3">
            <label className="flex items-center gap-2">
              <Checkbox
                checked={setOtherEnabled}
                onCheckedChange={(v) => setSetOtherEnabled(!!v)}
              />
              <span className="font-medium">Set Other Staff</span>
            </label>
            {setOtherEnabled && (
              <>
                <div className="flex gap-2 text-xs">
                  <label className="flex items-center gap-1">
                    <input type="radio" checked={otherMode === 'replace'} onChange={() => setOtherMode('replace')} />
                    Replace
                  </label>
                  <label className="flex items-center gap-1">
                    <input type="radio" checked={otherMode === 'add'} onChange={() => setOtherMode('add')} />
                    Add to existing
                  </label>
                </div>
                {otherStaff.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {otherStaff.map((id) => {
                      const e = employees.find((x: any) => x.id === id);
                      return (
                        <Badge key={id} variant="secondary" className="text-[10px]">
                          {e?.fullName || id}
                        </Badge>
                      );
                    })}
                  </div>
                )}
                <div className="max-h-48 overflow-auto border rounded p-1 space-y-1">
                  {employees.map((e: any) => (
                    <label key={e.id} className="flex items-center gap-2 text-xs px-2 py-1 hover:bg-accent/40 rounded cursor-pointer">
                      <Checkbox checked={otherStaff.includes(e.id)} onCheckedChange={() => toggleOther(e.id)} />
                      <span>{e.fullName || e.email || e.id}</span>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => apply.mutate()} disabled={apply.isPending || nothingToApply}>
            {apply.isPending ? 'Applying…' : `Apply to ${taskIds.length}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TaskEditDialog({ task, settingId, onClose, onSaved }: {
  task: any;
  settingId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Normalize a ref to its string id whether it arrives as a populated object or already a string.
  const refId = (v: any): string => {
    if (!v) return '';
    if (typeof v === 'string') return v;
    if (typeof v === 'object') return v.id ?? v._id ?? '';
    return '';
  };
  const refIds = (arr: any): string[] =>
    (Array.isArray(arr) ? arr : []).map(refId).filter(Boolean);

  const [draft, setDraft] = React.useState<any>({
    pointName: task.pointName ?? '',
    pointDescription: task.pointDescription ?? '',
    stage: task.stage ?? '',
    sortOrder: task.sortOrder ?? 0,
    fieldType: task.fieldType ?? 'checkbox',
    fieldOptions: Array.isArray(task.fieldOptions) ? task.fieldOptions.join(', ') : '',
    refEndpoint: task.refEndpoint ?? '',
    refLabel: task.refLabel ?? '',
    required: !!task.required,
    assetField: task.assetField ?? '',
    responsiblePerson: refId(task.responsiblePerson),
    otherStaff: refIds(task.otherStaff),
  });

  const { data: assetFields = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ['async-options', '/meta/model-fields/asset'],
    queryFn: async () => (await api<{ id: string; name: string }[]>('/meta/model-fields/asset')).data || [],
  });
  const { data: employees = [] } = useQuery<any[]>({
    queryKey: ['ref', '/employees', EMPLOYEE_REF_QUERY],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '500', ...EMPLOYEE_REF_QUERY });
      return (await api<any[]>(`/employees?${params.toString()}`)).data || [];
    },
  });
  // Stages are owned by the parent Configuration (AssetSetting.stages). Sibling
  // tasks may also reference stage names, so we union both for the picker.
  const settingKey = ['/asset-settings', settingId];
  const { data: setting } = useQuery<any>({
    queryKey: settingKey,
    queryFn: async () => (await api<any>(`/asset-settings/${settingId}`)).data,
  });
  const { data: siblings = [] } = useQuery<Array<{ stage: string | null }>>({
    queryKey: ['/asset-setting-tasks', settingId, 'stages'],
    queryFn: async () => (await api<Array<{ stage: string | null }>>(`/asset-setting-tasks?setting=${settingId}&limit=500`)).data || [],
  });
  const existingStages = React.useMemo(() => {
    const set = new Set<string>();
    for (const s of (setting?.stages ?? [])) set.add(s);
    for (const s of siblings) if (s.stage) set.add(s.stage);
    if (draft.stage) set.add(draft.stage);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [setting, siblings, draft.stage]);

  // Persist newly-created stages onto the parent Configuration so they're remembered.
  const qc = useQueryClient();
  const upsertStage = useMutation({
    mutationFn: async (newStage: string) => {
      const current: string[] = setting?.stages ?? [];
      if (current.includes(newStage)) return setting;
      const next = [...current, newStage];
      const r = await api(`/asset-settings/${settingId}`, { method: 'PUT', body: JSON.stringify({ stages: next }) });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: settingKey }),
    onError: (e: any) => toast('Error saving stage', e.message, 'error'),
  });

  const save = useMutation({
    mutationFn: async () => {
      const body: any = {
        pointName: draft.pointName,
        pointDescription: draft.pointDescription || null,
        stage: draft.stage || null,
        sortOrder: Number(draft.sortOrder) || 0,
        fieldType: draft.fieldType,
        fieldOptions: draft.fieldType === 'select'
          ? String(draft.fieldOptions).split(',').map((s: string) => s.trim()).filter(Boolean)
          : [],
        refEndpoint: draft.fieldType === 'ref' ? (draft.refEndpoint || null) : null,
        refLabel:    draft.fieldType === 'ref' ? (draft.refLabel || null) : null,
        required: !!draft.required,
        assetField: draft.assetField || null,
        responsiblePerson: draft.responsiblePerson || null,
        otherStaff: draft.otherStaff,
      };
      const r = await api(`/asset-setting-tasks/${task.id}`, { method: 'PUT', body: JSON.stringify(body) });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
    },
    onSuccess: () => { toast('Task saved', undefined, 'success'); onSaved(); },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  function toggleOther(id: string) {
    setDraft((d: any) => ({
      ...d,
      otherStaff: d.otherStaff.includes(id) ? d.otherStaff.filter((x: string) => x !== id) : [...d.otherStaff, id],
    }));
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Edit Task</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          <div>
            <Label>Point Name</Label>
            <Input value={draft.pointName} onChange={(e) => setDraft({ ...draft, pointName: e.target.value })} />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={2} value={draft.pointDescription} onChange={(e) => setDraft({ ...draft, pointDescription: e.target.value })} />
          </div>
          <div className="grid grid-cols-[1fr_120px] gap-3">
            <div>
              <Label>Stage (wizard step grouping)</Label>
              <Select
                value={draft.stage || '__none__'}
                onValueChange={(v) => {
                  if (v === '__none__') return setDraft({ ...draft, stage: '' });
                  if (v === '__new__') {
                    const name = window.prompt('Name the new stage:')?.trim();
                    if (name) {
                      setDraft({ ...draft, stage: name });
                      upsertStage.mutate(name);
                    }
                    return;
                  }
                  setDraft({ ...draft, stage: v });
                }}
              >
                <SelectTrigger><SelectValue placeholder="— Pick or create —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— No stage —</SelectItem>
                  {existingStages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  <SelectItem value="__new__" className="font-medium">+ Create new stage…</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Order #</Label>
              <Input type="number" value={draft.sortOrder} onChange={(e) => setDraft({ ...draft, sortOrder: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Input Type</Label>
              <Select value={draft.fieldType} onValueChange={(v) => setDraft({ ...draft, fieldType: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TASK_FIELD_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Updates Asset Field</Label>
              <LookupField
                endpoint="/meta/model-fields/asset"
                labelKey="name"
                value={draft.assetField || null}
                onChange={(id) => setDraft({ ...draft, assetField: id ?? '' })}
                placeholder="— None (compliance only) —"
              />
            </div>
          </div>
          {draft.fieldType === 'select' && (
            <div>
              <Label>Options (comma-separated)</Label>
              <Input value={draft.fieldOptions} onChange={(e) => setDraft({ ...draft, fieldOptions: e.target.value })} placeholder="Cellular, Satellite" />
            </div>
          )}
          {draft.fieldType === 'ref' && (
            <div className="space-y-3 rounded-md border p-3 bg-muted/30">
              <div>
                <Label>Lookup From</Label>
                <Select
                  value={draft.refEndpoint || '__custom__'}
                  onValueChange={(v) => {
                    if (v === '__custom__') return;
                    const preset = REF_ENDPOINT_PRESETS.find((p) => p.value === v);
                    setDraft({ ...draft, refEndpoint: v, refLabel: preset?.refLabel ?? draft.refLabel });
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Pick a collection…" /></SelectTrigger>
                  <SelectContent>
                    {REF_ENDPOINT_PRESETS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Endpoint (override)</Label>
                  <Input value={draft.refEndpoint} onChange={(e) => setDraft({ ...draft, refEndpoint: e.target.value })} placeholder="/master-data/sites" />
                </div>
                <div>
                  <Label className="text-xs">Display Field</Label>
                  <Input value={draft.refLabel} onChange={(e) => setDraft({ ...draft, refLabel: e.target.value })} placeholder="name" />
                </div>
              </div>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={!!draft.required} onCheckedChange={(c) => setDraft({ ...draft, required: !!c })} />
            Required
          </label>
          <div>
            <Label>Responsible Person</Label>
            <LookupField
              endpoint="/employees"
              labelKey="fullName"
              filter={EMPLOYEE_REF_QUERY}
              value={draft.responsiblePerson || null}
              onChange={(id) => setDraft({ ...draft, responsiblePerson: id ?? '' })}
              placeholder="— None —"
            />
          </div>
          <div>
            <Label className="mb-2 block">Other Staff</Label>
            {draft.otherStaff.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {draft.otherStaff.map((id: string) => {
                  const e = employees.find((emp: any) => emp.id === id);
                  return (
                    <span key={id} className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs">
                      {e?.fullName || id}
                      <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => toggleOther(id)}>×</button>
                    </span>
                  );
                })}
              </div>
            )}
            <div className="max-h-40 overflow-y-auto rounded-md border p-2 space-y-1">
              {employees.map((e: any) => (
                <label key={e.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={draft.otherStaff.includes(e.id)} onCheckedChange={() => toggleOther(e.id)} />
                  <span>{e.fullName || e.email}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- API Keys ----------------

// API key management moved to its own page: pages/admin/developer.tsx
// (route /admin/developer, resource slug 'api_keys'). It lived here as a
// hidden Settings section that no one could reach — the server gate it
// depended on compared a system-profile value that did not exist. That gate
// is gone; the `api_keys` resource grant is the only control now.

// ---------------- Integrations ----------------

// ---------------- Run Sheet Schedules ----------------
//
// The recipient list is operational PII (who gets the dispatch sheet emailed
// to them), so this section is reachable only via Settings, which is gated on
// the `settings_page` resource. An older comment here claimed the server also
// checked `systemProfile === 'Administrator'` — no such check ever existed,
// and that value was never a valid profile.

const RUN_SHEET_SCOPE_OPTIONS = [
  { value: 'today',       label: 'Today' },
  { value: 'tomorrow',    label: 'Tomorrow' },
  { value: 'next-7-days', label: 'Next 7 days' },
] as const;

function fmtTimestamp(v: string | null | undefined): string {
  return fmtDateTime(v);
}

// Friendly schedule builder (Frequency + Time + Days → cron) lives in
// `components/shared/schedule-builder.tsx` — shared with the recurring-
// dispatch toggle on the Dispatch form.

function RunSheetSchedulesSection() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { data: schedules = [] } = useQuery({
    queryKey: ['/run-sheet-schedules'],
    queryFn: async () => (await api<any[]>('/run-sheet-schedules')).data || [],
  });
  const [editing, setEditing] = React.useState<any | null>(null);

  const save = useMutation({
    mutationFn: async (body: any) => {
      const { id, _id, __v, createdAt, updatedAt, lastRunAt, lastRunStatus, lastRunError, ...payload } = body;
      // Normalise refs: refs may be populated objects on read; flatten to ids before write.
      if (Array.isArray(payload.recipients)) payload.recipients = payload.recipients.map((r: any) => typeof r === 'string' ? r : r?.id ?? r?._id).filter(Boolean);
      if (Array.isArray(payload.vehicleGroups)) payload.vehicleGroups = payload.vehicleGroups.map((r: any) => typeof r === 'string' ? r : r?.id ?? r?._id).filter(Boolean);
      if (payload.branch && typeof payload.branch === 'object') payload.branch = payload.branch.id ?? payload.branch._id ?? null;
      const url = id ? `/run-sheet-schedules/${id}` : '/run-sheet-schedules';
      const r = await api(url, { method: id ? 'PUT' : 'POST', body: JSON.stringify(payload) });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
    },
    onSuccess: () => {
      toast('Saved', undefined, 'success');
      qc.invalidateQueries({ queryKey: ['/run-sheet-schedules'] });
      setEditing(null);
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });
  const del = useMutation({
    mutationFn: async (id: string) => {
      const r = await api(`/run-sheet-schedules/${id}`, { method: 'DELETE' });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['/run-sheet-schedules'] }),
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Run Sheet Schedules</CardTitle>
        <CardDescription>
          Recurring run-sheet emails. Pick how often each schedule fires (daily, weekdays,
          chosen days of the week, or once a month) and the dispatcher emails every recipient
          a link to the run sheet for the chosen date window. Only admins can see or edit
          who's on the recipient list.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button size="sm" onClick={() => setEditing({ name: '', cron: '0 6 * * *', timezone: 'Pacific/Auckland', scope: 'today', active: true, recipients: [], vehicleGroups: [], ccEmails: [] })}>
          <Plus className="h-4 w-4" /> New schedule
        </Button>

        {schedules.length === 0 && (
          <div className="border rounded-md px-3 py-3 text-sm text-muted-foreground">No schedules configured.</div>
        )}

        {schedules.length > 0 && (
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs">
                <tr>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">When it runs</th>
                  <th className="text-left px-3 py-2">Scope</th>
                  <th className="text-left px-3 py-2">Recipients</th>
                  <th className="text-left px-3 py-2">Last run</th>
                  <th className="w-24"></th>
                </tr>
              </thead>
              <tbody>
                {schedules.map((s: any) => {
                  const recipientCount = (s.recipients?.length ?? 0) + (s.ccEmails?.length ?? 0);
                  return (
                    <tr key={s.id} className={`border-t ${s.active ? '' : 'opacity-60'}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium">{s.name}</div>
                        {!s.active && <Badge variant="secondary" className="text-[10px]">Disabled</Badge>}
                      </td>
                      <td className="px-3 py-2 text-xs" title={s.cron}>{describeCron(parseCron(s.cron ?? ''))}</td>
                      <td className="px-3 py-2 capitalize">{(s.scope ?? 'today').replace(/-/g, ' ')}</td>
                      <td className="px-3 py-2">
                        {recipientCount > 0 ? `${recipientCount} ${recipientCount === 1 ? 'person' : 'people'}` : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <div>{fmtTimestamp(s.lastRunAt)}</div>
                        {s.lastRunStatus === 'failed' && (
                          <div className="text-destructive truncate max-w-[260px]" title={s.lastRunError ?? ''}>⚠ {s.lastRunError || 'Failed'}</div>
                        )}
                        {s.lastRunStatus === 'ok' && (
                          <Badge variant="success" className="text-[10px]">OK</Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button className="text-primary hover:underline text-xs mr-3" onClick={() => setEditing({ ...s })}>Edit</button>
                        <button
                          className="text-destructive hover:underline text-xs"
                          onClick={async () => { if (await confirm({ title: `Remove ${s.name}?`, description: 'The schedule will stop emailing immediately.', destructive: true, confirmLabel: 'Remove' })) del.mutate(s.id); }}
                        >Remove</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit schedule' : 'New schedule'}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Name</Label>
                <Input value={editing.name ?? ''} onChange={(e) => setEditing((p: any) => ({ ...p, name: e.target.value }))} />
              </div>
              <ScheduleBuilder
                cron={editing.cron ?? '0 6 * * *'}
                onChange={(c) => setEditing((p: any) => ({ ...p, cron: c }))}
              />
              {/* Timezone is fixed to Pacific/Auckland (model default).
                  The field was operator-editable but free-text, so most
                  rows held the default anyway. Drop the input — model
                  still carries the value for future per-schedule overrides
                  if we ever need them. */}
              <div>
                <Label className="text-xs">Which day's run sheet to send</Label>
                <Select value={editing.scope ?? 'today'} onValueChange={(v) => setEditing((p: any) => ({ ...p, scope: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RUN_SHEET_SCOPE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Controls which day(s) the emailed run sheet covers — independent of when the schedule fires.
                  E.g. a 6am daily run with "Today" sends the morning brief; a 5pm daily run with "Tomorrow" sends the next-day prep.
                </p>
              </div>
              <div>
                <Label className="text-xs">Recipients (Employees)</Label>
                <LookupRecipients
                  value={editing.recipients ?? []}
                  onChange={(ids) => setEditing((p: any) => ({ ...p, recipients: ids }))}
                />
              </div>
              <div>
                <Label className="text-xs">Additional email addresses (one per line)</Label>
                <Textarea
                  rows={3}
                  value={(editing.ccEmails ?? []).join('\n')}
                  onChange={(e) => setEditing((p: any) => ({ ...p, ccEmails: e.target.value.split(/\r?\n/).map((s) => s.trim()).filter(Boolean) }))}
                  placeholder="ops@example.com"
                />
              </div>
              <div>
                <Label className="text-xs">Email subject (optional)</Label>
                <Input value={editing.emailSubject ?? ''} onChange={(e) => setEditing((p: any) => ({ ...p, emailSubject: e.target.value || null }))} placeholder="Run Sheet — {date}" />
              </div>
              <div>
                <Label className="text-xs">Email body (optional)</Label>
                <Textarea rows={3} value={editing.emailMessage ?? ''} onChange={(e) => setEditing((p: any) => ({ ...p, emailMessage: e.target.value || null }))} placeholder="Today's run sheet is ready." />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={!!editing.active} onCheckedChange={(c) => setEditing((p: any) => ({ ...p, active: c === true }))} />
                <span>Active (uncheck to pause without deleting)</span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={() => editing && save.mutate(editing)} disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/** Multi-select Employee picker — wraps the shared MultiLookupField with
 *  the EMPLOYEE_REF_QUERY so archived people don't show up. */
function LookupRecipients({ value, onChange }: { value: any[]; onChange: (ids: string[]) => void }) {
  return (
    <MultiLookupField
      endpoint="/employees"
      labelKey="fullName"
      value={value}
      onChange={onChange}
      filter={EMPLOYEE_REF_QUERY as Record<string, string>}
    />
  );
}

/** Catalogue of external API integrations the system can talk to.
 *  Each integration declares one or more `fields`, every field stored as
 *  its own `AppSetting` row. Sensitive fields render as password inputs
 *  and are encrypted at rest by the server's pre-save hook.
 *
 *  When wiring a new integration into the runtime, read its key via
 *  the existing /master-data/app-settings endpoint (or a server-side
 *  helper that decrypts on read). Never log the raw value. */
interface IntegrationField {
  settingKey: string;
  label: string;
  placeholder: string;
  /** Render as a password input + redact on read. Defaults to true — the
   *  whole point of this UI is credentials. Set to false only for non-
   *  secret companion fields like a base URL. */
  sensitive?: boolean;
}
interface IntegrationDef {
  /** Used in the test endpoint URL (/integrations/<id>/test) and to group
   *  fields under a single card. Must match the prefix on each
   *  `settingKey` so the server's `useStored` test mode can find them. */
  id: string;
  label: string;
  description: string;
  docsUrl?: string;
  fields: IntegrationField[];
  /** Brand logo component (see `integration-logos.tsx`). Receives a
   *  className from the parent for sizing; renders its own brand colour
   *  + mark so the tile doesn't need a separate `accent` class. */
  Logo: React.ComponentType<LogoProps>;
}

const INTEGRATIONS: IntegrationDef[] = [
  {
    id: 'nzta',
    label: 'NZTA (via Carjam)',
    description: 'Vehicle / WoF / CoF / registration lookups by NZ plate. Rotate at the NZTA / Carjam portal if compromised.',
    docsUrl: 'https://www.carjam.co.nz/',
    Logo: NztaLogo,
    fields: [
      { settingKey: 'nzta.apiKey', label: 'API Key', placeholder: 'Paste NZTA / Carjam API key' },
    ],
  },
  {
    id: 'eroad',
    label: 'EROAD',
    description: 'Telematics provider for the active fleet. Two API tokens — one for the Vehicles endpoint (read fleet roster + state) and one for the Maintenance endpoint (read service schedules). Org / Fleet IDs are required only for Maintenance reads.',
    docsUrl: 'https://developer.eroad.com/',
    Logo: EroadLogo,
    fields: [
      { settingKey: 'eroad.apiToken',           label: 'Vehicles API Token',     placeholder: 'header: ApiKey' },
      { settingKey: 'eroad.vehiclesApiBase',    label: 'Vehicles API Base URL',  placeholder: 'https://api.apac.eroad.com', sensitive: false },
      { settingKey: 'eroad.maintenanceKey',     label: 'Maintenance API Key',    placeholder: 'header: eroad-api-key' },
      { settingKey: 'eroad.maintenanceApiBase', label: 'Maintenance API Base URL', placeholder: 'https://api.eroad.co.nz', sensitive: false },
      { settingKey: 'eroad.orgId',              label: 'Org ID',                 placeholder: 'EROAD org identifier', sensitive: false },
      { settingKey: 'eroad.fleetId',            label: 'Fleet ID',               placeholder: 'EROAD "All Vehicles" fleet id', sensitive: false },
    ],
  },
  {
    id: 'blackhawk',
    label: 'Blackhawk',
    description: 'Telematics provider for the secondary fleet. Bearer token + tenant-specific base URL.',
    Logo: BlackhawkLogo,
    fields: [
      { settingKey: 'blackhawk.apiToken', label: 'API Token',     placeholder: 'header: Authorization: Bearer …' },
      { settingKey: 'blackhawk.apiBase',  label: 'API Base URL',  placeholder: 'https://…/blackhawk', sensitive: false },
    ],
  },
  {
    id: 'smtp',
    label: 'Email (SMTP)',
    description: 'SMTP relay for outbound email — the default transport (used unless Email Delivery is set to SendGrid). Saved values here override the SMTP_* env vars. Leave the password blank for relays that don\'t require auth.',
    Logo: SmtpLogo,
    fields: [
      { settingKey: 'smtp.host', label: 'Host',     placeholder: 'smtp.example.com', sensitive: false },
      { settingKey: 'smtp.port', label: 'Port',     placeholder: '587 (STARTTLS) or 465 (TLS)', sensitive: false },
      { settingKey: 'smtp.user', label: 'Username', placeholder: 'SMTP account / API user', sensitive: false },
      { settingKey: 'smtp.pass', label: 'Password', placeholder: 'SMTP password / API key' },
    ],
  },
  {
    id: 'sendgrid',
    label: 'Email (SendGrid)',
    description: 'Outbound transactional email — magic links, invitations, run-sheet dispatches, IoT notifications. Falls back to SMTP if EMAIL_PROVIDER is not "sendgrid". A saved key here overrides the SENDGRID_API_KEY env var.',
    docsUrl: 'https://app.sendgrid.com/settings/api_keys',
    Logo: SendgridLogo,
    fields: [
      { settingKey: 'sendgrid.apiKey',    label: 'API Key',        placeholder: 'SG.xxxxxxxx' },
      { settingKey: 'sendgrid.fromEmail', label: 'Default From',   placeholder: 'no-reply@atlasconcrete.co.nz', sensitive: false },
    ],
  },
  {
    id: 'kudosity',
    label: 'SMS (Kudosity)',
    description: 'Outbound SMS — job-card / dispatch notifications via the Kudosity (TransmitSMS) API. A saved key here overrides the KUDOSITY_* env vars.',
    docsUrl: 'https://developer.kudosity.com/',
    Logo: KudosityLogo,
    fields: [
      { settingKey: 'kudosity.apiKey',    label: 'API Key',     placeholder: 'Kudosity API key' },
      { settingKey: 'kudosity.apiSecret', label: 'API Secret',  placeholder: 'Kudosity API secret' },
      { settingKey: 'kudosity.senderId',  label: 'Sender ID',   placeholder: 'e.g. 3768 or a brand name', sensitive: false },
    ],
  },
];

/** An integration is considered active when every sensitive field has a
 *  saved value. Non-sensitive companion fields (base URLs, org ids) don't
 *  by themselves prove the connection works, so we don't gate on them. */
function isIntegrationActive(cfg: IntegrationDef, hasByKey: Record<string, boolean>): boolean {
  const required = cfg.fields.filter((f) => f.sensitive !== false);
  if (required.length === 0) return false;
  return required.every((f) => !!hasByKey[f.settingKey]);
}

/** Last-4 mask: shows the trailing characters so the admin can verify
 *  which key is saved without exposing the full secret. */
/** Render the placeholder for a stored secret. The server never returns the
 *  plaintext for secret-key settings, so we cannot show even the last 4
 *  characters — just a fixed-width string of bullets to communicate "a
 *  value is on file" without leaking entropy. */
function maskKey(hasValue: boolean): string {
  return hasValue ? '••••••••••••' : '—';
}

/** Top-level integrations page. Drives a small state machine between four
 *  sub-views; each delegates rendering to a focused child component:
 *
 *    list   → grid of active integrations + "Add new connection" tile
 *    detail → expanded view of one active integration (manage / disconnect)
 *    add    → picker showing every integration not yet active
 *    setup  → setup form for one chosen integration; on save → back to list
 *
 *  Active vs available is computed off the live AppSetting query so the
 *  list reflects the DB state without manual sync — adding/removing
 *  credentials anywhere flips the integration into the right bucket on
 *  the next render. */
function IntegrationsSection() {
  type View =
    | { kind: 'list' }
    | { kind: 'detail'; id: string }
    | { kind: 'add' }
    | { kind: 'setup'; id: string };
  const [view, setView] = React.useState<View>({ kind: 'list' });

  // Load every AppSetting whose key starts with an integration id prefix
  // in one shot, so we can determine which integrations are active.
  const { data: settings } = useQuery({
    queryKey: ['/master-data/app-settings', 'integrations'],
    queryFn: async () => {
      const r = await api<any[]>(`/master-data/app-settings?limit=200`);
      return r.data ?? [];
    },
  });
  const hasByKey: Record<string, boolean> = React.useMemo(() => {
    const map: Record<string, boolean> = {};
    for (const s of settings ?? []) map[s.key] = !!s.hasValue;
    return map;
  }, [settings]);

  const active = INTEGRATIONS.filter((c) => isIntegrationActive(c, hasByKey));
  const available = INTEGRATIONS.filter((c) => !isIntegrationActive(c, hasByKey));

  const selectedCfg = (view.kind === 'detail' || view.kind === 'setup')
    ? INTEGRATIONS.find((c) => c.id === view.id) ?? null
    : null;

  if (view.kind === 'detail' && selectedCfg) {
    return (
      <IntegrationDetailView
        cfg={selectedCfg}
        onBack={() => setView({ kind: 'list' })}
        onDisconnected={() => setView({ kind: 'list' })}
      />
    );
  }
  if (view.kind === 'add') {
    return (
      <IntegrationPickerView
        available={available}
        onBack={() => setView({ kind: 'list' })}
        onPick={(id) => setView({ kind: 'setup', id })}
      />
    );
  }
  if (view.kind === 'setup' && selectedCfg) {
    return (
      <IntegrationSetupView
        cfg={selectedCfg}
        onBack={() => setView({ kind: 'add' })}
        onSaved={() => setView({ kind: 'list' })}
      />
    );
  }

  // List view (default). Empty state nudges the operator straight into
  // the Add flow when nothing is connected yet.
  return (
    <div className="space-y-4">
      {active.length === 0 ? (
        <div className="rounded-md border border-dashed bg-muted/30 p-8 text-center space-y-3">
          <Plug className="h-8 w-8 mx-auto text-muted-foreground" />
          <div>
            <div className="font-medium">No active integrations</div>
            <p className="text-sm text-muted-foreground">
              Connect EROAD, Blackhawk, NZTA, or another service to enable live data flows.
            </p>
          </div>
          <Button size="sm" onClick={() => setView({ kind: 'add' })}>
            <Plus className="h-4 w-4" /> Add new connection
          </Button>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-muted-foreground">Active integrations ({active.length})</h3>
            <Button size="sm" onClick={() => setView({ kind: 'add' })}>
              <Plus className="h-4 w-4" /> Add new connection
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {active.map((cfg) => (
              <IntegrationTile
                key={cfg.id}
                cfg={cfg}
                onClick={() => setView({ kind: 'detail', id: cfg.id })}
                connected
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Compact card used in both the active-list and the picker. Clickable
 *  surface that shows the integration's icon + name + a status pill. */
function IntegrationTile({
  cfg, onClick, connected,
}: {
  cfg: IntegrationDef;
  onClick: () => void;
  connected?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group text-left rounded-lg border bg-card hover:bg-accent/40 hover:border-primary/40 transition-colors p-4 flex items-center gap-3"
    >
      <cfg.Logo className="h-10 w-10" />
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{cfg.label}</div>
        <div className="text-xs text-muted-foreground line-clamp-1">{cfg.description}</div>
      </div>
      {connected ? (
        <Badge variant="success" className="shrink-0">Connected</Badge>
      ) : (
        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 group-hover:text-foreground" />
      )}
    </button>
  );
}

/** Detail view for an already-connected integration. Re-uses the same
 *  IntegrationForm as the setup flow but with `mode='manage'` so the
 *  Disconnect button is rendered. */
function IntegrationDetailView({
  cfg, onBack, onDisconnected,
}: {
  cfg: IntegrationDef;
  onBack: () => void;
  onDisconnected: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" /> Back to integrations
        </Button>
      </div>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <cfg.Logo className="h-10 w-10" />
            <div className="flex-1 min-w-0">
              <CardTitle className="text-base">{cfg.label}</CardTitle>
              <CardDescription>{cfg.description}</CardDescription>
            </div>
            <Badge variant="success" className="shrink-0">Connected</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <IntegrationForm cfg={cfg} mode="manage" onDisconnected={onDisconnected} onSaved={() => { /* stay in detail */ }} />
        </CardContent>
      </Card>
    </div>
  );
}

/** Picker view — every integration that isn't currently active. Empty
 *  state appears when the operator has already connected everything we
 *  know how to talk to. */
function IntegrationPickerView({
  available, onBack, onPick,
}: {
  available: IntegrationDef[];
  onBack: () => void;
  onPick: (id: string) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
      </div>
      <div>
        <h3 className="text-sm font-medium">Choose an integration to connect</h3>
        <p className="text-xs text-muted-foreground">Only services we've built support for are listed here.</p>
      </div>
      {available.length === 0 ? (
        <div className="rounded-md border border-dashed bg-muted/30 p-8 text-center">
          <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-600 mb-2" />
          <div className="font-medium">All available integrations are connected.</div>
          <p className="text-sm text-muted-foreground">
            Nothing left to wire up. Manage existing connections from the list.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {available.map((cfg) => (
            <IntegrationTile key={cfg.id} cfg={cfg} onClick={() => onPick(cfg.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Setup view for a freshly-picked integration. Same form as the detail
 *  view minus the Disconnect affordance; on a successful save we bounce
 *  back to the active list so the new card appears immediately. */
function IntegrationSetupView({
  cfg, onBack, onSaved,
}: {
  cfg: IntegrationDef;
  onBack: () => void;
  onSaved: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" /> Back to picker
        </Button>
      </div>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <cfg.Logo className="h-10 w-10" />
            <div className="flex-1 min-w-0">
              <CardTitle className="text-base">Connect {cfg.label}</CardTitle>
              <CardDescription>
                {cfg.description}
                {cfg.docsUrl && (
                  <>
                    {' '}
                    <a href={cfg.docsUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                      Docs ↗
                    </a>
                  </>
                )}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <IntegrationForm cfg={cfg} mode="setup" onSaved={onSaved} onDisconnected={() => { /* not reachable in setup */ }} />
        </CardContent>
      </Card>
    </div>
  );
}

/** State for a single field within a multi-field integration form. The
 *  `setting` row is fetched once and cached by useSetting; we only track
 *  the in-progress draft and a per-field show/hide toggle locally. */
interface FieldState {
  draft: string;
  show: boolean;
}

/** Shared form rendered by both Setup and Detail views. Encapsulates the
 *  per-field state, save/test/disconnect mutations, and the inline test
 *  result pill. `mode` flips Disconnect visibility and the primary CTA
 *  label. */
function IntegrationForm({
  cfg, mode, onSaved, onDisconnected,
}: {
  cfg: IntegrationDef;
  mode: 'setup' | 'manage';
  onSaved: () => void;
  onDisconnected: () => void;
}) {
  const qc = useQueryClient();
  const confirm = useConfirm();

  // Per-field settings + draft state. useSetting cannot be called in a
  // loop conditionally, so we call it for every declared field at the
  // top level — order is stable across renders because `cfg.fields`
  // is a module-level constant.
  const settings = cfg.fields.map((f) => useSetting(f.settingKey));
  const [drafts, setDrafts] = React.useState<Record<string, FieldState>>(() => {
    const o: Record<string, FieldState> = {};
    for (const f of cfg.fields) o[f.settingKey] = { draft: '', show: false };
    return o;
  });
  const setFieldDraft = (key: string, patch: Partial<FieldState>) =>
    setDrafts((p) => ({ ...p, [key]: { ...p[key], ...patch } }));

  const hasAnyDraft = cfg.fields.some((f) => drafts[f.settingKey]?.draft.length > 0);
  const hasAllSaved = cfg.fields.filter((f) => f.sensitive !== false).every((f, i) => !!(settings[i] as any)?.hasValue);
  const hasAnySaved = settings.some((s) => !!(s as any)?.hasValue);

  // Inline test-connection state. `null` = haven't tested yet on this
  // card; otherwise carries the latest result so the admin sees an
  // immediate success/failure pill below the form.
  const [testResult, setTestResult] = React.useState<{ ok: boolean; message: string; latencyMs: number } | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      // Save each dirty field. Use the existing app-settings PUT/POST
      // (one row per key) so the server's encryption hook fires per save.
      for (const f of cfg.fields) {
        const v = drafts[f.settingKey]?.draft ?? '';
        if (!v) continue;
        const existing = settings[cfg.fields.indexOf(f)] as any;
        const body = { key: f.settingKey, value: v };
        const r = existing?.id
          ? await api(`/master-data/app-settings/${existing.id}`, { method: 'PUT', body: JSON.stringify(body) })
          : await api('/master-data/app-settings', { method: 'POST', body: JSON.stringify(body) });
        if (!r.success) throw new Error(r.error?.message || `Failed to save ${f.label}`);
      }
    },
    onSuccess: () => {
      toast('Saved', `${cfg.label} updated`, 'success');
      qc.invalidateQueries({ queryKey: ['/master-data/app-settings'] });
      const reset: Record<string, FieldState> = {};
      for (const f of cfg.fields) reset[f.settingKey] = { draft: '', show: false };
      setDrafts(reset);
      setTestResult(null);
      onSaved();
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  const clear = useMutation({
    mutationFn: async () => {
      for (let i = 0; i < cfg.fields.length; i++) {
        const s: any = settings[i];
        if (!s?.id) continue;
        const r = await api(`/master-data/app-settings/${s.id}`, { method: 'DELETE' });
        if (!r.success) throw new Error(r.error?.message || `Failed to remove ${cfg.fields[i].label}`);
      }
    },
    onSuccess: () => {
      toast('Disconnected', `${cfg.label} removed`, 'success');
      qc.invalidateQueries({ queryKey: ['/master-data/app-settings'] });
      setTestResult(null);
      onDisconnected();
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  /** Ping the upstream API once and surface the result. Sends the
   *  unsaved draft when one is present (lets admins verify a key before
   *  committing); falls back to `useStored: true` so the server pulls,
   *  decrypts, and pings the saved values without round-tripping them
   *  through the UI. */
  const test = useMutation({
    mutationFn: async () => {
      const values: Record<string, string> = {};
      let hasDraft = false;
      for (const f of cfg.fields) {
        const d = drafts[f.settingKey]?.draft;
        if (d) { values[f.settingKey] = d; hasDraft = true; }
      }
      const body = hasDraft ? { values } : { useStored: true };
      const r = await api<any>(`/master-data/integrations/${cfg.id}/test`, { method: 'POST', body: JSON.stringify(body) });
      if (!r.success) throw new Error(r.error?.message || 'Test request failed');
      return r.data as { ok: boolean; message: string; latencyMs: number };
    },
    onSuccess: (result) => setTestResult(result),
    onError: (e: any) => setTestResult({ ok: false, message: e?.message ?? String(e), latencyMs: 0 }),
  });

  return (
    <div className="space-y-4">
      {cfg.fields.map((f, idx) => {
          const setting = settings[idx] as any;
          const state = drafts[f.settingKey] ?? { draft: '', show: false };
          const sensitive = f.sensitive !== false;
          const hasSaved = !!setting?.hasValue;
          return (
            <div key={f.settingKey} className="space-y-1.5">
              <div className="flex items-baseline gap-2">
                <Label className="text-xs font-medium">{f.label}</Label>
                {sensitive
                  ? <span className="text-[10px] text-muted-foreground">(encrypted)</span>
                  : null}
                {hasSaved && (
                  <Badge variant="success" className="ml-auto shrink-0 text-[10px]">Saved</Badge>
                )}
              </div>
              {sensitive && hasSaved && (
                // For sensitive saved values the server never returns the
                // plaintext, so render a fixed-width mask — the admin
                // confirms presence without leaking entropy.
                <code className="block font-mono text-xs bg-muted/40 border rounded px-2 py-1">
                  {maskKey(true)}
                </code>
              )}
              {!sensitive && hasSaved && (
                // Non-sensitive fields (base URLs, org ids) come back from
                // the redaction-free path — display the saved value as a
                // read-only mirror so the admin can verify it.
                <code className="block font-mono text-xs bg-muted/40 border rounded px-2 py-1 truncate" title={setting?.value ?? ''}>
                  {setting?.value || '—'}
                </code>
              )}
              <div className="flex gap-2">
                <Input
                  type={sensitive && !state.show ? 'password' : 'text'}
                  value={state.draft}
                  onChange={(e) => setFieldDraft(f.settingKey, { draft: e.target.value })}
                  placeholder={hasSaved ? `Replace ${f.label.toLowerCase()}` : f.placeholder}
                  // Chrome / Edge ignore `autocomplete="off"` on password
                  // inputs and helpfully fill the field with the user's
                  // saved app password — which is how a token field ends
                  // up reading "changeme123!" without anyone typing it.
                  // `new-password` is the documented opt-out the password
                  // managers actually honour. A randomised `name` further
                  // foils heuristic matchers that key on the field name.
                  autoComplete="new-password"
                  name={`int-${f.settingKey}-${Math.random().toString(36).slice(2, 8)}`}
                  data-form-type="other"
                  className="font-mono text-xs"
                />
                {sensitive && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setFieldDraft(f.settingKey, { show: !state.show })}
                    disabled={state.draft.length === 0}
                  >
                    {state.show ? 'Hide' : 'Show'}
                  </Button>
                )}
              </div>
            </div>
          );
        })}

        {testResult && (
          // Inline pill — colour depends on the last ping. Keeps the
          // result visible until the admin saves / cancels rather than
          // popping a toast that scrolls away.
          <div
            className={
              'rounded-md border px-3 py-2 text-xs flex items-start gap-2 ' +
              (testResult.ok
                ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200'
                : 'border-rose-300 bg-rose-50 text-rose-800 dark:border-rose-700 dark:bg-rose-950/40 dark:text-rose-200')
            }
          >
            <span className="font-semibold">{testResult.ok ? 'OK' : 'Failed'}</span>
            <span className="flex-1">{testResult.message}</span>
            {testResult.latencyMs > 0 && (
              <span className="opacity-70">{testResult.latencyMs}ms</span>
            )}
          </div>
        )}

      <div className="flex items-center gap-2 pt-1">
        <Button
          size="sm"
          disabled={!hasAnyDraft || save.isPending}
          onClick={() => save.mutate()}
        >
          <Save className="h-4 w-4" />{' '}
          {mode === 'setup'
            ? (save.isPending ? 'Connecting…' : 'Connect')
            : (save.isPending ? 'Saving…' : (hasAllSaved ? 'Replace' : 'Save'))}
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={(!hasAnyDraft && !hasAnySaved) || test.isPending}
          onClick={() => test.mutate()}
        >
          <CheckCircle2 className="h-4 w-4" /> {test.isPending ? 'Testing…' : 'Test connection'}
        </Button>
        {mode === 'manage' && hasAnySaved && (
          <Button
            size="sm"
            variant="outline"
            disabled={clear.isPending}
            onClick={async () => {
              if (await confirm({
                title: `Disconnect ${cfg.label}?`,
                description: 'Any features that depend on these credentials will stop working until you reconnect.',
                destructive: true,
                confirmLabel: 'Disconnect',
              })) clear.mutate();
            }}
            className="ml-auto"
          >
            <XCircle className="h-4 w-4" /> Disconnect
          </Button>
        )}
      </div>
    </div>
  );
}
